'use client';

import { useState } from 'react';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/forms/button';
import { Textarea } from '@/components/ui/forms/textarea';
import { Dialog, DialogContent, DialogFooter } from '@/components/ui/layout/dialog';
import { DialogHeading } from '@/components/ui/design-system';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/forms/select';
import { Label } from '@/components/ui/forms/label';

type NoteType = 'general' | 'interview' | 'task' | 'follow_up';

interface AddNoteDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (content: string, type: NoteType) => Promise<void>;
  contactName?: string;
}

export function AddNoteDialog({
  open,
  onOpenChange,
  onSave,
  contactName,
}: AddNoteDialogProps) {
  const [content, setContent] = useState('');
  const [noteType, setNoteType] = useState<NoteType>('general');
  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    if (!content.trim()) {
      return;
    }

    setIsSaving(true);
    try {
      await onSave(content, noteType);
      // Reset form
      setContent('');
      setNoteType('general');
      onOpenChange(false);
    } catch (error) {
      console.error('Failed to save note:', error);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    if (isSaving) return;
    setContent('');
    setNoteType('general');
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeading
          title="Add Note"
          description={`Add a private note about ${contactName || 'this contact'}.`}
        />

        <div className="space-y-4 py-2">
          <div className="space-y-2">
            <Label htmlFor="note-type" className="text-overline">
              Note Type
            </Label>
            <Select
              value={noteType}
              onValueChange={(value) => setNoteType(value as NoteType)}
            >
              <SelectTrigger id="note-type" className="border">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="general">General</SelectItem>
                <SelectItem value="interview">Interview</SelectItem>
                <SelectItem value="task">Task</SelectItem>
                <SelectItem value="follow_up">Follow Up</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="note-content" className="text-overline">
              Note Content
            </Label>
            <Textarea
              id="note-content"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Enter your note here..."
              className="border min-h-[200px] resize-y"
              disabled={isSaving}
            />
            <p className="text-caption text-muted-foreground">
              {content.length} characters
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            className="border"
            onClick={handleCancel}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            className="border"
            style={{ boxShadow: 'var(--shadow-warm-sm)' }}
            onClick={handleSave}
            disabled={isSaving || !content.trim()}
          >
            {isSaving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              'Save Note'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
